import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Bot, Loader2, Menu } from 'lucide-react';
import Sidebar from './Sidebar.tsx';
import WhatsAppQrModal from '../configuration/WhatsAppQrModal.tsx';
import { useAuthSession } from '../../../../../contexts/AuthSessionContext';
import { hasActiveAtendabotWhatsappTrial } from '../../../../../utils/billing';
import { ensureActiveSession } from '../../../../../services/session';
import { isAtendabotWhatsAppUnavailable } from '../../utils/whatsappStatus.ts';
import { useAtendabotOrderControl } from '../../hooks/useAtendabotOrderControl.ts';
import { useAtendabotRestaurant } from '../../hooks/useAtendabotRestaurant.ts';
import { useAtendabotWhatsAppGate } from '../../hooks/useAtendabotWhatsAppGate.ts';
import { useAtendabotSidebarChrome } from '../../hooks/useAtendabotSidebarChrome.ts';
import {
  AtendabotLayoutContext,
  useAtendabotLayout,
} from '../../contexts/AtendabotLayoutContext.tsx';

export { useAtendabotLayout };

interface LayoutProps {
  children?: any;
}

const LayoutShell = ({ children }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const reconnectRequested = useRef(false);
  const [reconnectDismissed, setReconnectDismissed] = useState(false);
  const isPromptRoute = location.pathname.startsWith('/atendabot/prompt');
  const isWhatsAppRoute = location.pathname.startsWith('/atendabot/whatsapp');
  const isAgendaRoute = location.pathname.startsWith('/atendabot/agenda');
  const isConversationsRoute = location.pathname.startsWith('/atendabot/conversations');
  const wasConversationsRoute = useRef(isConversationsRoute);
  const {
    session,
    user,
    restaurant,
    hasProductAccess,
    isFoodUser,
  } = useAuthSession();

  const restaurantContext = useMemo(() => ({
    restaurante_id: restaurant?.id || null,
    isAdmin: user?.isAdmin === true,
  }), [restaurant?.id, user?.isAdmin]);

  const {
    restaurante,
    setRestaurante,
    restaurantCacheKey,
    loadingRestaurant,
    promptSourceResolved,
    promptDraftPending,
    promptReadyToPublish,
    hasInstanceToken,
    usesManagerPrompt,
  } = useAtendabotRestaurant({
    restauranteId: restaurantContext?.restaurante_id,
    isAdmin: restaurantContext?.isAdmin,
  });

  const { orderControlEnabled } = useAtendabotOrderControl({
    isFoodUser,
    restauranteId: restaurantContext?.restaurante_id,
  });

  const {
    isDesktop,
    mobileMenuOpen,
    setMobileMenuOpen,
    sidebarOpen,
    setSidebarOpen,
  } = useAtendabotSidebarChrome(isConversationsRoute);

  useEffect(() => {
    if (isConversationsRoute && !wasConversationsRoute.current) {
      setSidebarOpen(false);
    }
    wasConversationsRoute.current = isConversationsRoute;
  }, [isConversationsRoute, setSidebarOpen]);

  const isLegalRoute =
    location.pathname.startsWith('/atendabot/termos-de-uso')
    || location.pathname.startsWith('/atendabot/politica-de-privacidade');
  const canAccessAtendabotWithTrial =
    hasProductAccess('atendabot', { allowTrialRestricted: true });
  const hasConfiguredTrial = hasInstanceToken
    && hasActiveAtendabotWhatsappTrial(session)
    && hasProductAccess('atendabot');
  const bypassProvisioningGate =
    isPromptRoute
    || isWhatsAppRoute
    || (isLegalRoute && canAccessAtendabotWithTrial);

  const onWhatsAppConfigured = useCallback(() => {
    if (canAccessAtendabotWithTrial && !hasProductAccess('atendabot')) {
      void ensureActiveSession({ force: true }).catch(() => {});
    }
    setRestaurante((current: any) => {
      const next = { ...(current || {}), whatsapp_instance_configured: true };
      try {
        sessionStorage.setItem(restaurantCacheKey, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  }, [canAccessAtendabotWithTrial, hasProductAccess, restaurantCacheKey, setRestaurante]);

  const {
    zapiStatus,
    hasZapiCheck,
    checkingZapiStatus,
    qrImage,
    qrExpired,
    loadingQr,
    isBlocked,
    isZapiUnavailable,
    isZapiOffline,
    showOverlay,
    loadWhatsappQrCode,
  } = useAtendabotWhatsAppGate({
    restauranteId: restaurantContext?.restaurante_id,
    hasInstanceToken,
    loadingRestaurant,
    bypassProvisioningGate,
    onConfigured: onWhatsAppConfigured,
  });

  const connectionPending = !loadingRestaurant && hasInstanceToken && !hasZapiCheck;
  const reconnectNeeded = isZapiOffline && zapiStatus?.configured !== false;
  const showReconnectModal = reconnectNeeded && !reconnectDismissed;
  useEffect(() => {
    if (!reconnectNeeded) {
      reconnectRequested.current = false;
      setReconnectDismissed(false);
      return;
    }
    if (!showReconnectModal) return;
    if (reconnectRequested.current) return;
    reconnectRequested.current = true;
    void loadWhatsappQrCode();
  }, [reconnectNeeded, showReconnectModal, loadWhatsappQrCode]);
  const initialLoading = loadingRestaurant || connectionPending;
  const canExploreTrial = hasConfiguredTrial
    && zapiStatus?.configured !== false
    && !isAtendabotWhatsAppUnavailable(zapiStatus);
  const whatsappDisconnected = !loadingRestaurant && (
    !hasInstanceToken
    || !hasZapiCheck
    || (
      isAtendabotWhatsAppUnavailable(zapiStatus)
      || zapiStatus?.configured === false
      || (!canExploreTrial && (
        zapiStatus?.connected !== true || zapiStatus?.smartphoneConnected === false
      ))
    )
  );

  const layoutValue = useMemo(() => ({
    promptSourceResolved,
    usesManagerPrompt,
    restaurante,
    setRestaurante,
    loadingRestaurant,
    promptDraftPending,
    promptReadyToPublish,
    hasInstanceToken,
    orderControlEnabled,
    showOverlay,
    isBlocked,
    isZapiUnavailable,
    checkingZapiStatus,
    qrImage,
    qrExpired,
    loadingQr,
    loadWhatsappQrCode,
    isDesktop,
    sidebarOpen,
    mobileMenuOpen,
    setSidebarOpen,
    setMobileMenuOpen,
  }), [
    promptSourceResolved,
    usesManagerPrompt,
    restaurante,
    setRestaurante,
    loadingRestaurant,
    promptDraftPending,
    promptReadyToPublish,
    hasInstanceToken,
    orderControlEnabled,
    showOverlay,
    isBlocked,
    isZapiUnavailable,
    checkingZapiStatus,
    qrImage,
    qrExpired,
    loadingQr,
    loadWhatsappQrCode,
    isDesktop,
    sidebarOpen,
    mobileMenuOpen,
    setSidebarOpen,
    setMobileMenuOpen,
  ]);

  return (
    <AtendabotLayoutContext.Provider value={layoutValue}>
      {initialLoading ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#1c1a55] via-[#322f84] to-[#4f46a5] px-6 text-white" role="status" aria-live="polite">
          <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-indigo-400/20 blur-3xl" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-32 -right-20 h-96 w-96 rounded-full bg-violet-300/20 blur-3xl" aria-hidden="true" />
          <div className="relative flex w-full max-w-sm flex-col items-center rounded-3xl border border-white/20 bg-white/10 px-8 py-10 text-center shadow-2xl backdrop-blur-sm">
            <div className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
              <Loader2 size={64} className="absolute text-white/30 motion-safe:animate-spin" strokeWidth={1.5} aria-hidden="true" />
              <Bot size={30} className="text-white" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Carregando AtendaBot</h1>
            <p className="mt-2 text-sm leading-6 text-indigo-100">Preparando seu painel e verificando a conexão do WhatsApp...</p>
          </div>
        </div>
      ) : (
        <div className="relative flex h-screen bg-gray-50">
          <Sidebar
            restauranteSlug={restaurante?.checkin_slug || null}
            hidePrompt={!promptSourceResolved}
            promptDraftPending={promptDraftPending}
            promptNotReadyToPublish={!promptReadyToPublish}
            orderControlEnabled={orderControlEnabled}
            whatsappDisconnected={whatsappDisconnected}
            open={sidebarOpen}
            mobileOpen={mobileMenuOpen}
            onToggle={() => {
              if (!isDesktop) {
                setMobileMenuOpen((prev) => !prev);
              } else {
                setSidebarOpen((prev) => !prev);
              }
            }}
            onNavigate={() => setMobileMenuOpen(false)}
          />
          {!isDesktop && mobileMenuOpen && (
            <button
              type="button"
              aria-label="Fechar menu"
              onClick={() => setMobileMenuOpen(false)}
              className="fixed inset-0 z-30 bg-black/40 md:hidden"
            />
          )}
          {!isDesktop && !mobileMenuOpen && (
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menu"
              className="fixed top-3 left-3 z-50 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition-all hover:bg-slate-50 active:scale-95 md:hidden"
            >
              <Menu size={22} />
            </button>
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <main className={`flex-1 overflow-y-auto ${isAgendaRoute ? 'p-0' : 'p-4 md:p-6'}`}>
              {whatsappDisconnected && !bypassProvisioningGate && !showReconnectModal
                ? <Navigate to="/atendabot/whatsapp" replace />
                : children}
            </main>
          </div>
        </div>
      )}
      <WhatsAppQrModal
        open={showReconnectModal}
        title="Reconecte o WhatsApp"
        description="A conexão do WhatsApp foi interrompida. Escaneie o QR Code para retomar o atendimento."
        image={qrImage}
        phoneNumber={zapiStatus?.phone_number}
        loading={loadingQr}
        error={qrExpired ? 'O QR Code expirou. Gere um novo para conectar.' : undefined}
        onRetry={loadWhatsappQrCode}
        closeLabel={hasConfiguredTrial ? 'Continuar explorando o painel' : undefined}
        onClose={() => hasConfiguredTrial ? setReconnectDismissed(true) : navigate('/atendabot/whatsapp')}
      />
    </AtendabotLayoutContext.Provider>
  );
};

const Layout = ({ children }: LayoutProps) => {
  const hasPersistentLayout = useContext(AtendabotLayoutContext);
  if (hasPersistentLayout) return <>{children}</>;
  return <LayoutShell>{children}</LayoutShell>;
};

export default Layout;
